import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { LeadsDetailsService } from '../../../services/leads-details-service.service';
import { QuickApprovalService } from '../../../services/quick-approval.service';
import { LeadsUpdateStatusModal } from '../../../components/shared/leads-update-status-modal/leads-update-status-modal';
import { LeadContextService } from '../../../services/lead-context.service';
import { LocalStorageUtil } from '../../../utils/local-storage.util';
import { ReguaSegment } from '../../../interfaces/quick-approval.interface';
import {
  catchError,
  defer,
  finalize,
  Observable,
  of,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { SelectedDnService } from '../../../services/selected-dn.service';
import { ConfigService } from '../../../services/config.service';
import { LeadsDetailsPa } from './components/leads-details-pa/leads-details-pa';
import { LeadsDetailsPr } from './components/leads-details-pr/leads-details-pr';
import { LeadsDetailsSs } from './components/leads-details-ss/leads-details-ss';
import { LeadsDetailsSp } from './components/leads-details-sp/leads-details-sp';
import { LeadsDetailsSo } from './components/leads-details-so/leads-details-so';
import { LeadsDetailsCc } from './components/leads-details-cc/leads-details-cc';

@Component({
  selector: 'app-leads-details',
  standalone: true,
  imports: [
    LeadsDetailsPa,
    LeadsDetailsPr,
    LeadsDetailsSs,
    LeadsDetailsSp,
    LeadsDetailsSo,
    LeadsDetailsCc,
  ],
  templateUrl: './leads-details.html',
  styleUrls: ['./leads-details.scss'],
})
export class LeadsDetails implements OnInit, OnDestroy {
  private dnSubscription?: Subscription;
  private leadDetailsSubscription?: Subscription;
  private quickApprovalSubscription?: Subscription;
  private readonly destroy$ = new Subject<void>();

  isLoading: boolean = true;
  errorMessage: string | null = null;
  leadId: string | null = null;
  cdSiglaTipo: string | null = null; // SO, PA, PR
  leadDetails: any = null;
  reguaSegments: ReguaSegment[] = [];
  sliderLoading: boolean = true;
  exibirReguaQuickApproval: boolean = false;

  sliderValue: number = 0;

  constructor(
    private leadsDetailsService: LeadsDetailsService,
    private quickApprovalService: QuickApprovalService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private leadContextService: LeadContextService,
    private selectedDnService: SelectedDnService,
    private configService: ConfigService,
  ) {}

  ngOnInit() {
    // Capturar leadId da rota ou query params (compatibilidade)
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.leadId = params['leadId'] || null;
      if (this.leadId) {
        this.loadLeadDetails();
      }
    });

    // Também verificar query params para compatibilidade
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((queryParams) => {
      if (!this.leadId && queryParams['idLead']) {
        this.leadId = queryParams['idLead'];
        this.loadLeadDetails();
      }
    });

    // Fallback apenas se não tiver nem rota nem query param
    if (!this.leadId) {
      // Para testes: usar leadId padrão apenas se não vier de nenhuma fonte
      if (this.configService.isDebugEnabled()) {
        console.warn('LeadId não encontrado na rota nem query params');
      }
    }

    // Primeiro configurar a subscription do DN
    this.dnSubscription = this.selectedDnService.selectedDn$
      .pipe(takeUntil(this.destroy$))
      .subscribe((dn) => {
        if (this.configService.isDebugEnabled()) {
          console.log('Dashboard - DN changed:', dn);
        }
        if (dn && !this.isLoading) {
          // Se já tivermos feito a carga inicial, recarregar tudo
          this.router.navigate(['/dashboard']);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dnSubscription?.unsubscribe();
    this.leadDetailsSubscription?.unsubscribe();
    this.quickApprovalSubscription?.unsubscribe();
  }

  loadLeadDetails() {
    this.leadDetailsSubscription?.unsubscribe();
    this.quickApprovalSubscription?.unsubscribe();

    if (!this.leadId) {
      this.errorMessage = 'Lead ID não encontrado';
      this.isLoading = false;
      this.sliderLoading = false;
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.sliderLoading = true;
    this.exibirReguaQuickApproval = false;
    this.reguaSegments = [];

    this.leadsDetailsService
      .getLeadDetails(this.leadId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (details) => {
          if (this.configService.isDebugEnabled()) {
            console.log('Detalhes do lead:', details);
          }
          this.leadDetails = {
            ...details,
            renovaMais: this.obterStatusRenovaMais(details),
          };

          this.cdSiglaTipo = this.leadDetails.cdSiglaTipo ?? null;

          // Atualizar contexto para breadcrumb
          this.leadContextService.setCdSiglaTipo(this.cdSiglaTipo);

          this.deveExibirReguaQuickApproval(details);

          this.sliderValue = details.percentualEntrada ?? 0;
          this.isLoading = false;

          if (this.exibirReguaQuickApproval) {
            this.sliderLoading = true;
            this.quickApprovalSubscription = this.buscarQuickApproval(details)
              .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                  this.sliderLoading = false;
                }),
              )
              .subscribe({
                next: (quickApprovalDetails) => {
                  if (this.configService.isDebugEnabled()) {
                    console.log('✅ Segmentos da régua obtidos:', quickApprovalDetails);
                  }
                  this.reguaSegments = quickApprovalDetails;
                },
              });
          } else {
            this.sliderLoading = false;
          }

          if (['SP', 'SS', 'CC'].includes(this.getLayoutType() ?? '')) {
            this.leadContextService.hasNavigationBar.emit(false);
          }
        },
        error: (error) => {
          console.error('Erro ao carregar detalhes:', error);
          if (error.status === 404) {
            this.errorMessage = 'Lead não encontrada';
          } else if (error.status === 500) {
            this.errorMessage = 'Erro no servidor. Tente novamente.';
          } else {
            this.errorMessage = 'Erro ao carregar dados. Tente novamente.';
          }
          this.sliderLoading = false;
        },
      });
  }

  navigateToOfferPage() {
    this.router.navigate(['/leads/leads-offers']);
  }

  onContactClick(): void {
    if (this.leadId) {
      this.router.navigate(['leads', this.leadId, 'leads-offers']);
    }
    // Ou usar do leadDetails se preferir
    else if (this.leadDetails?.idLead) {
      this.router.navigate(['leads', this.leadDetails.idLead, 'leads-offers']);
    } else {
      console.error('Lead ID não encontrado');
      alert('Não foi possível identificar o lead');
    }
  }

  // Método para determinar qual layout usar
  getLayoutType(): 'PA' | 'PR' | 'SS' | 'SP' | 'SO' | 'CC' | null {
    if (!this.cdSiglaTipo) return null;

    const tipo = this.cdSiglaTipo.toUpperCase();

    // Verificar se é PR (pode ser "PR" ou "CAPR000001" ou similar)
    if (tipo === 'PR' || tipo.startsWith('CAPR') || tipo.includes('PR')) {
      return 'PR';
    }

    if (tipo === 'PA' || tipo === 'SS' || tipo === 'SP' || tipo === 'CC') {
      return tipo;
    }

    return 'SO';
  }

  // Método para obter classe do badge de status
  getStatusBadgeClass(status: string): string {
    if (!status) return 'status-badge-default';
    const statusLower = status.toLowerCase().trim();
    if (statusLower.includes('pendente') || statusLower.includes('novo')) {
      return 'status-badge-yellow';
    } else if (statusLower.includes('negociação') || statusLower.includes('negociacao')) {
      return 'status-badge-green';
    }
    return 'status-badge-default';
  }

  // Método para abrir modal de alterar status
  openChangeStatusModal(): void {
    if (!this.leadDetails?.idLead) return;

    this.dialog.open(LeadsUpdateStatusModal, {
      width: 'auto',
      maxWidth: '95vw',
      panelClass: 'dialog-alterar-status',
      data: { idLead: this.leadDetails.idLead },
    });
  }

  onSliderValueChange(value: number) {
    this.sliderValue = value;
    if (this.configService.isDebugEnabled()) {
      console.log('Slider value changed:', value);
    }
  }

  gerarNovaSimulacao() {
    const token = LocalStorageUtil.getAccessToken();
    const codigo = LocalStorageUtil.getSelectedVWDn();
    const leadId = this.leadDetails?.idLead;
    const simulacaoId = this.leadDetails?.codigoSimulacao;
    const dataSimulacao = this.leadDetails?.dataSimulacao;

    // Usar template do environment
    const platformSimulationUrl = this.configService.getRequiredByPath<string>(
      'bancoVw.platformSimulationUrl',
    );

    let url = platformSimulationUrl
      .replace('{token}', token || '')
      .replace('{codigo}', codigo || '')
      .replace('{leadId}', leadId || '');

    // Se não houver simulação, remover o parâmetro idSimulacao da URL
    if (dataSimulacao === null) {
      url = url.replace('&idSimulacao={simulacaoId}', '');
    } else {
      url = url.replace('{simulacaoId}', simulacaoId || '');
    }

    window.open(url, '_self');
  }

  // Adicione este método na classe LeadsDetails
  navigateToPage(page: number) {
    if (this.leadId) {
      // Usar rota com leadId
      const routesWithId = [
        `/leads/${this.leadId}/detalhes`,
        `/leads/${this.leadId}/leads-offers`,
        `/leads/${this.leadId}/leads-history`,
      ];
      this.router.navigate([routesWithId[page]]);
    } else {
      // Fallback para rotas antigas
      const pageRoutes = ['/leads/leads-info', '/leads/leads-offers', '/leads/leads-history'];
      this.router.navigate([pageRoutes[page]]);
    }
  }

  private obterStatusRenovaMais(details: any): boolean {
    const flagRenovacao = details?.flRenovacao ?? details?.flagRenovacao ?? details?.flag_renovacao;

    if (typeof flagRenovacao === 'string') {
      return flagRenovacao.trim().toUpperCase() === 'S';
    }

    if (typeof details?.renovaMais === 'string') {
      return details.renovaMais.trim().toUpperCase() === 'S';
    }

    return details?.renovaMais === true;
  }

  private deveExibirReguaQuickApproval(details: any): void {
    const tipoCampanha =
      details?.tipoCampanhaId ?? details?.idTipoCampanha ?? details?.campaignTypeId;
    const isTipoCampanha1 = Number(tipoCampanha) === 1;

    const siglaTipo = (details?.cdSiglaTipo || '').toString().trim().toUpperCase();
    const isSimulacaoOnline = siglaTipo === 'SO' || isTipoCampanha1;

    const preAprovado = this.obterFlagPreAprovacao(details);
    const preAprovadoValido = preAprovado === 'S' || preAprovado === 'N';

    this.exibirReguaQuickApproval = isSimulacaoOnline && preAprovadoValido;
  }

  private obterFlagPreAprovacao(details: any): string {
    const preAprovRaw =
      details?.preAprovado ??
      details?.PreAprovado ??
      details?.FL_PRE_APROV ??
      details?.flPreAprov ??
      details?.fl_pre_aprov;

    if (preAprovRaw === null || preAprovRaw === undefined) {
      return '';
    }

    return String(preAprovRaw).trim().toUpperCase();
  }

  /**
   * Busca os dados da régua de aprovação via API Quick Approval
   */
  private buscarQuickApproval(details: any): Observable<ReguaSegment[]> {
    // Verificar se tem dados necessários
    if (!details.cpf || !details.dataNascimento) {
      if (this.configService.isDebugEnabled()) {
        console.warn('Dados insuficientes para Quick Approval');
      }
      return of([]);
    }

    return defer(() => {
      const request = this.quickApprovalService.buildQuickApprovalRequest(details);
      return this.quickApprovalService.getQuickApproval(request);
    }).pipe(
      catchError((error) => {
        console.error('Falha ao carregar Quick Approval; a régua não será exibida.', error);
        return of([]);
      }),
    );
  }
}
